import { useState, useEffect } from 'react';
import { adminApi } from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { formatRelativeTime, getKYCTierInfo } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { Check, X, Clock, FileText, User, Shield } from 'lucide-react';

const AdminKYCPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);

  useEffect(() => {
    fetchKYCQueue();
  }, []);

  const fetchKYCQueue = async () => {
    try {
      const res = await adminApi.getKYCQueue();
      setSubmissions(res.data);
    } catch (error) {
      toast.error('Failed to load KYC queue');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (submissionId) => {
    try {
      await adminApi.approveKYC(submissionId);
      toast.success('KYC approved');
      fetchKYCQueue();
    } catch (error) {
      toast.error('Failed to approve KYC');
    }
  };

  const handleReject = async (submissionId) => {
    try {
      await adminApi.rejectKYC(submissionId, 'Documents unclear');
      toast.success('KYC rejected');
      fetchKYCQueue();
    } catch (error) {
      toast.error('Failed to reject KYC');
    }
  };

  const getWaitTimeColor = (createdAt) => {
    const hours = (Date.now() - new Date(createdAt).getTime()) / 3600000;
    if (hours < 24) return 'text-green-500';
    if (hours < 48) return 'text-yellow-500';
    return 'text-red-500';
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              KYC Review Queue
            </h1>
            <p className="text-muted-foreground">Review and approve user verifications</p>
          </div>
          <div className="text-sm text-muted-foreground">
            {submissions.length} pending reviews
          </div>
        </div>
        
        {/* Queue Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-card border border-white/10 rounded-xl p-4">
            <p className="text-2xl font-bold">{submissions.length}</p>
            <p className="text-sm text-muted-foreground">In Queue</p>
          </div>
          <div className="bg-card border border-white/10 rounded-xl p-4">
            <p className="text-2xl font-bold text-green-500">
              {submissions.filter(s => {
                const hours = (Date.now() - new Date(s.created_at).getTime()) / 3600000;
                return hours < 24;
              }).length}
            </p>
            <p className="text-sm text-muted-foreground">&lt; 24 hours</p>
          </div>
          <div className="bg-card border border-white/10 rounded-xl p-4">
            <p className="text-2xl font-bold text-yellow-500">
              {submissions.filter(s => s.tier_requested === 2).length}
            </p>
            <p className="text-sm text-muted-foreground">Tier 2 Requests</p>
          </div>
          <div className="bg-card border border-white/10 rounded-xl p-4">
            <p className="text-2xl font-bold text-purple-500">
              {submissions.filter(s => s.tier_requested === 3).length}
            </p>
            <p className="text-sm text-muted-foreground">Tier 3 Requests</p>
          </div>
        </div>
        
        {/* Submissions List */}
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-32 w-full rounded-xl" />
            ))}
          </div>
        ) : submissions.length === 0 ? (
          <div className="bg-card border border-white/10 rounded-xl p-12 text-center">
            <Shield className="w-12 h-12 mx-auto mb-4 text-green-500" />
            <h3 className="font-semibold mb-2">Queue is empty</h3>
            <p className="text-muted-foreground">All KYC submissions have been reviewed</p>
          </div>
        ) : (
          <div className="space-y-4">
            {submissions.map((submission) => {
              const tierInfo = getKYCTierInfo(submission.tier_requested);
              const waitTimeColor = getWaitTimeColor(submission.created_at);
              
              return (
                <div
                  key={submission.submission_id}
                  className="bg-card border border-white/10 rounded-xl p-6"
                  data-testid={`kyc-${submission.submission_id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                        <User className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{submission.user_name || submission.full_name}</h3>
                        <p className="text-sm text-muted-foreground">
                          Current: Tier {submission.current_tier || 1} → Requesting: Tier {submission.tier_requested}
                        </p>
                      </div>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full ${tierInfo.bgColor} ${tierInfo.color}`}>
                      Tier {submission.tier_requested}
                    </span>
                  </div>
                  
                  {/* Details */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">ID Type</p>
                      <p className="font-medium capitalize">{submission.id_type?.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Country</p>
                      <p className="font-medium">{submission.country}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Submitted</p>
                      <p className={`font-medium ${waitTimeColor}`}>
                        {formatRelativeTime(submission.created_at)}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">DOB</p>
                      <p className="font-medium">{submission.date_of_birth}</p>
                    </div>
                  </div>
                  
                  {/* Document previews (placeholders) */}
                  <div className="flex gap-4 mb-4">
                    <div className="w-24 h-16 bg-white/5 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <div className="w-24 h-16 bg-white/5 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-muted-foreground" />
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-3">
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleApprove(submission.submission_id)}
                      data-testid={`approve-kyc-${submission.submission_id}`}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleReject(submission.submission_id)}
                      data-testid={`reject-kyc-${submission.submission_id}`}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                    <Button variant="outline">
                      Request More Info
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminKYCPage;
